import ReactGA from 'react-ga4';
import { getAllConfigs } from '../services/configService';
import {
  getStoredConsent,
  hasAnalyticsConsent,
  hasMarketingConsent,
  isAdminPath,
  isAnalyticsEnabledByEnv
} from './analyticsConsent';

let initPromise = null;

// Google Ads destination for the already-loaded gtag.js instance. Kept at module
// scope so a later marketing opt-in can still attach it without re-initializing.
let adsId = null;
let adsConfigured = false;
// Conversion label for the Ads-side purchase action. Without it we can only
// rely on the GA4 import chain, which is what left the account with no signal.
let adsPurchaseLabel = null;

const configureAdsTagIfAllowed = (consent) => {
  const marketingGranted = consent ? Boolean(consent.marketing) : hasMarketingConsent();

  if (adsConfigured || !adsId || !marketingGranted || isAdminPath()) {
    return false;
  }

  // Second destination on the gtag.js instance react-ga4 already loaded. We never
  // inject another script; gtag.js itself fetches its Ads companion after this.
  ReactGA.gtag('config', adsId, { allow_enhanced_conversions: true });
  adsConfigured = true;
  return true;
};

const toConsentSignals = (consent) => ({
  analytics_storage: consent?.analytics ? 'granted' : 'denied',
  ad_storage: consent?.marketing ? 'granted' : 'denied',
  ad_user_data: consent?.marketing ? 'granted' : 'denied',
  ad_personalization: consent?.marketing ? 'granted' : 'denied'
});

const doInitialize = async () => {
  try {
    const configResponse = await getAllConfigs();
    const gaId = configResponse?.data?.googleAnalyticsId;

    if (!configResponse?.success || !gaId || !gaId.startsWith('G-')) {
      console.warn('GA4 not initialized: missing or invalid googleAnalyticsId in config');
      return false;
    }

    if (!isAnalyticsEnabledByEnv() || !hasAnalyticsConsent() || isAdminPath()) {
      return false;
    }

    ReactGA.gtag('consent', 'default', toConsentSignals(getStoredConsent()));

    const debugMode = process.env.NODE_ENV !== 'production';
    ReactGA.initialize(gaId, {
      gaOptions: {
        send_page_view: false,
        ...(debugMode ? { debug_mode: true } : {})
      }
    });

    const configuredAdsId = configResponse?.data?.googleAdsId;
    adsId = typeof configuredAdsId === 'string' && configuredAdsId.startsWith('AW-')
      ? configuredAdsId
      : null;

    const configuredAdsLabel = configResponse?.data?.googleAdsPurchaseLabel;
    adsPurchaseLabel = typeof configuredAdsLabel === 'string' && configuredAdsLabel.trim()
      ? configuredAdsLabel.trim()
      : null;

    configureAdsTagIfAllowed();

    if (!isAdminPath()) {
      ReactGA.send({
        hitType: 'pageview',
        page: window.location.pathname + window.location.search
      });
    }

    return true;
  } catch (error) {
    console.error('Error initializing GA4:', error);
    return false;
  }
};

export const initializeAnalyticsIfAllowed = () => {
  if (!isAnalyticsEnabledByEnv() || isAdminPath()) {
    return Promise.resolve(false);
  }

  if (ReactGA?.isInitialized) {
    // Marketing may have been accepted on admin, where AW configuration is
    // deferred. Apply current consent before attaching it on the storefront.
    if (!adsConfigured && adsId && hasMarketingConsent()) {
      applyConsentToAnalytics(getStoredConsent());
    }
    return Promise.resolve(true);
  }

  // Basic mode loads a new gtag only when analytics consent is also granted.
  if (!hasAnalyticsConsent()) {
    return Promise.resolve(false);
  }

  if (!initPromise) {
    initPromise = doInitialize().then((ok) => {
      if (!ok) {
        initPromise = null;
      }
      return ok;
    });
  }

  return initPromise;
};

export const applyConsentToAnalytics = (consent) => {
  if (ReactGA?.isInitialized) {
    ReactGA.gtag('consent', 'update', toConsentSignals(consent));
    // A visitor who accepts marketing after the tag loaded still gets the Ads
    // destination; gtag's consent state keeps it dormant until then.
    configureAdsTagIfAllowed(consent);
  }
};

// Native Ads purchase conversion, sent next to the GA4 `purchase` event. Keeping
// its own destination means Ads stops depending on the GA4 import to see a sale.
// The configured destination survives consent changes, so check current permission.
export const sendGoogleAdsPurchaseConversion = ({ value, currency, transactionId, userData } = {}) => {
  if (!isAnalyticsEnabledByEnv() || !hasMarketingConsent() || isAdminPath()) {
    return false;
  }

  if (!adsConfigured || !adsId || !adsPurchaseLabel) {
    return false;
  }

  if (!ReactGA?.isInitialized || typeof ReactGA.gtag !== 'function') {
    return false;
  }

  // Enhanced conversions. gtag.js hashes these fields with SHA-256 in the browser,
  // so no raw customer data leaves the page. Must be set before the event.
  if (userData) {
    ReactGA.gtag('set', 'user_data', userData);
  }

  ReactGA.gtag('event', 'conversion', {
    send_to: `${adsId}/${adsPurchaseLabel}`,
    value,
    currency,
    transaction_id: transactionId
  });

  return true;
};

export const __testing = {
  __resetAnalyticsInit: () => {
    initPromise = null;
    adsId = null;
    adsConfigured = false;
    adsPurchaseLabel = null;
  }
};
